// src/pages/markets/board/card/ActivityBlock.tsx

import { Box } from '@mui/material';
import { useTheme, type Theme } from '@mui/material/styles';
import type { EventActivity, MetricMode } from '../api/boardTypes';
import { CARD_TEST_IDS } from './cardLayout';
import { Figure } from '../../../../components/common/page/Figure';
import { CardTail, TailNote } from './CardFigures';
import { EM_DASH, formatCount, formatUsd, truncate } from './cardFormat';

export interface ActivityWindows {
    drift: string;
    jump: string;
}

export interface ActivityContext {
    activity: EventActivity | null;
    unit: string;
    metricMode: MetricMode;
    windows: ActivityWindows;
}

export interface ActivityBlockProps {
    ctx: ActivityContext;
    marketLabel?: string | null;
}

const MAX_MARKET_LABEL = 28;

// log_odds has no unit string, so the space in front of it has to go too.
function withUnit(text: string, unit: string): string {
    return unit ? `${text} ${unit}` : text;
}

export function formatActivitySigned(
    v: number | null | undefined,
    mode: MetricMode,
    unit: string,
): string {
    if (v == null || !Number.isFinite(v)) return EM_DASH;
    const digits = mode === 'log_odds' ? 2 : 1;
    return withUnit(`${v >= 0 ? '+' : ''}${v.toFixed(digits)}`, unit);
}

export function formatActivityUnsigned(
    v: number | null | undefined,
    mode: MetricMode,
    unit: string,
): string {
    if (v == null || !Number.isFinite(v)) return EM_DASH;
    const digits = mode === 'log_odds' ? 2 : 1;
    return withUnit(Math.abs(v).toFixed(digits), unit);
}

export function directionColor(v: number | null | undefined, theme: Theme): string {
    if (v == null || !Number.isFinite(v) || v === 0) return theme.palette.text.secondary;
    return v > 0 ? theme.palette.success.main : theme.palette.error.main;
}

function arrow(v: number | null | undefined): string {
    if (v == null || !Number.isFinite(v) || v === 0) return '';
    return v > 0 ? '▲' : '▼';
}

function clockTime(ts: string | null | undefined): string {
    if (!ts) return '';
    const t = new Date(ts);
    if (!Number.isFinite(t.getTime())) return '';
    return t.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
}

export interface ActivityRowMetric {
    value: number | null;
    caption: string;
}

// The collapsed row shows the sharpest thing that happened: a jump if there was
// one, otherwise the drift. The caption names the window it came from, so the
// number is never ambiguous between the two.
export function activityRowMetric(ctx: ActivityContext): ActivityRowMetric {
    const a = ctx.activity;
    if (!a) return { value: null, caption: '' };
    if (a.resolved) return { value: a.drift, caption: 'resolved' };
    if (a.jump != null && Number.isFinite(a.jump)) {
        return { value: a.jump, caption: ctx.windows.jump };
    }
    return { value: a.drift, caption: ctx.windows.drift };
}

function Slot({ children, testId }: { children: React.ReactNode; testId: string }) {
    return (
        <Box data-testid={testId} sx={{ minWidth: 0 }}>
            {children}
        </Box>
    );
}

export function ActivityBlock({ ctx, marketLabel }: ActivityBlockProps) {
    const theme = useTheme();
    const { activity, unit, metricMode, windows } = ctx;

    if (!activity) {
        return (
            <CardTail columns={1} testId={CARD_TEST_IDS.activity}>
                <TailNote testId={CARD_TEST_IDS.activityEmpty}>
                    No activity in this window
                </TailNote>
            </CardTail>
        );
    }

    const jumpAt = clockTime(activity.jump_at);
    const ratio = activity.volume_ratio;
    const hasRatio = ratio != null && Number.isFinite(ratio);
    const jumpSub = [jumpAt, marketLabel ? truncate(marketLabel, MAX_MARKET_LABEL) : '']
        .filter(Boolean)
        .join(' · ');
    const volumeSub = [
        hasRatio ? `${(ratio as number).toFixed(1)}×` : '',
        `${formatCount(activity.trade_count)} trades`,
    ]
        .filter(Boolean)
        .join(' · ');

    return (
        <CardTail columns={3} testId={CARD_TEST_IDS.activity}>
            <Slot testId={CARD_TEST_IDS.activityShift}>
                <Figure
                    dense
                    label={`Drift ${windows.drift}`}
                    value={`${arrow(activity.drift)}${formatActivitySigned(activity.drift, metricMode, unit)}`}
                    color={directionColor(activity.drift, theme)}
                    sub={activity.resolved ? 'resolved' : undefined}
                    subColor={activity.resolved ? theme.palette.warning.main : undefined}
                />
            </Slot>
            <Slot testId={CARD_TEST_IDS.activityJump}>
                <Figure
                    dense
                    label={`Jump ${windows.jump}`}
                    value={
                        activity.jump == null
                            ? EM_DASH
                            : formatActivitySigned(activity.jump, metricMode, unit)
                    }
                    color={
                        activity.jump == null
                            ? theme.palette.text.disabled
                            : directionColor(activity.jump, theme)
                    }
                    sub={activity.jump == null ? 'no jump' : jumpSub}
                    title={marketLabel ?? undefined}
                />
            </Slot>
            <Slot testId={CARD_TEST_IDS.activityVolume}>
                <Figure
                    dense
                    label={`Volume ${windows.drift}`}
                    value={formatUsd(activity.volume_now)}
                    sub={volumeSub}
                    subColor={
                        hasRatio && (ratio as number) >= 1
                            ? theme.palette.success.main
                            : undefined
                    }
                    title={`${formatUsd(activity.volume_now)} vs ${formatUsd(activity.volume_prev)} in the window before`}
                />
            </Slot>
        </CardTail>
    );
}
